import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown,
  ChevronRight,
  Target,
  MessageSquare,
  CheckCircle2,
  DollarSign,
  Users,
  Building2,
  Phone,
  Mail,
  Calendar,
  Zap,
  Shield,
  TrendingUp,
  Clock,
  AlertTriangle,
  Truck,
  Wrench,
  Package,
  ArrowLeft,
  Clipboard,
  FileText,
  Star,
  Quote,
  Play,
  ExternalLink,
  Settings,
  BarChart3,
  PieChart,
  Layers,
  Database,
  Lock,
  Smartphone,
  Globe,
  RefreshCw,
  BadgeCheck,
  CircleDollarSign,
  Receipt,
  LineChart,
  Brain,
  ArrowUpRight,
  Percent,
  AlertCircle,
  Workflow,
  Link2
} from "lucide-react";
import { Button } from "@/components/ui/button";

const STERILEMATE_LOGO = "https://sterilemate.com/wp-content/uploads/2023/01/sterile-mate-logo.webp";
const TECHNIQUEST_LOGO = "https://sterilemate.com/wp-content/uploads/2024/11/techniquest-logo.webp";

// Expandable Section Component
const Section = ({ title, icon: Icon, children, defaultOpen = false, color = "blue" }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  const colorClasses = {
    blue: "from-blue-500 to-cyan-500 text-blue-600",
    green: "from-emerald-500 to-teal-500 text-emerald-600",
    purple: "from-violet-500 to-purple-500 text-violet-600",
    orange: "from-orange-500 to-amber-500 text-orange-600",
    red: "from-red-500 to-rose-500 text-red-600",
    indigo: "from-indigo-500 to-blue-500 text-indigo-600"
  };
  
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden mb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-white hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 bg-gradient-to-br ${colorClasses[color]} rounded-lg flex items-center justify-center`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <span className="font-semibold text-slate-800 text-lg">{title}</span>
        </div>
        {isOpen ? (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronRight className="w-5 h-5 text-slate-400" />
        )}
      </button>
      {isOpen && (
        <div className="p-6 bg-slate-50 border-t border-slate-200">
          {children}
        </div>
      )}
    </div>
  );
};

// Script Card Component
const ScriptCard = ({ title, script, tips }) => (
  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
    <div className="p-4 bg-slate-800 text-white">
      <h5 className="font-semibold flex items-center gap-2">
        <MessageSquare className="w-4 h-4" />
        {title}
      </h5>
    </div>
    <div className="p-4">
      <p className="text-slate-700 italic mb-3">"{script}"</p>
      <div className="flex items-start gap-2 text-sm">
        <Star className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
        <p className="text-slate-600"><strong>Tip:</strong> {tips}</p>
      </div>
    </div>
  </div>
);

// Objection Handler Component
const ObjectionHandler = ({ objection, response }) => (
  <div className="bg-white rounded-lg border border-slate-200 p-4">
    <div className="flex items-start gap-3">
      <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
      <div>
        <p className="font-semibold text-slate-800 mb-2">"{objection}"</p>
        <p className="text-slate-600 text-sm">{response}</p>
      </div>
    </div>
  </div>
);

// Feature Card Component
const FeatureCard = ({ icon: Icon, title, description, color = "blue" }) => {
  const bgColors = {
    blue: "bg-blue-100",
    green: "bg-emerald-100",
    purple: "bg-violet-100",
    orange: "bg-amber-100",
    cyan: "bg-cyan-100",
    indigo: "bg-indigo-100"
  };
  const iconColors = {
    blue: "text-blue-600",
    green: "text-emerald-600",
    purple: "text-violet-600",
    orange: "text-amber-600",
    cyan: "text-cyan-600",
    indigo: "text-indigo-600"
  };
  
  return (
    <div className="bg-white rounded-xl p-5 border border-slate-200 hover:shadow-lg transition-shadow">
      <div className={`w-12 h-12 ${bgColors[color]} rounded-lg flex items-center justify-center mb-4`}>
        <Icon className={`w-6 h-6 ${iconColors[color]}`} />
      </div>
      <h5 className="font-bold text-slate-800 mb-2">{title}</h5>
      <p className="text-sm text-slate-600">{description}</p>
    </div>
  );
};

export default function SupplyChainPlaybook() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-900 via-purple-900 to-indigo-800 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin" className="flex items-center gap-2 text-indigo-200 hover:text-white transition-colors">
                <ArrowLeft className="w-5 h-5" />
                <span>Back to Dashboard</span>
              </Link>
            </div>
            <div className="flex items-center gap-4">
              <img src={TECHNIQUEST_LOGO} alt="Techniquest" className="h-10 object-contain bg-white rounded-lg px-3 py-1" />
            </div>
          </div>
        </div>
        
        {/* Hero Section */}
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="flex flex-col lg:flex-row items-center gap-8">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur rounded-full px-4 py-2 mb-6">
                <Database className="w-4 h-4 text-indigo-300" />
                <span className="text-sm text-indigo-200">Healthcare Supply Chain Software</span>
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold mb-4">
                Supply Chain Software<br />
                <span className="text-indigo-300">Sales Playbook</span>
              </h1>
              <p className="text-xl text-indigo-200 mb-6">
                Techinquest IMS + HART Suite: Fix implant billing and rebate gaps at their source.
              </p>
              <div className="flex flex-wrap gap-4">
                <a 
                  href="https://customer-assets.emergentagent.com/job_c4eb42da-4586-4303-b844-90350e5cf110/artifacts/cs7h8qvz_Techinquest%20Brochure%201.13.2026.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-3 bg-white text-indigo-700 rounded-lg hover:bg-indigo-50 transition-colors font-medium"
                >
                  <FileText className="w-5 h-5" />
                  Full Suite Brochure
                </a>
                <a 
                  href="https://sterilemate.com/implant-management-system/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-3 bg-indigo-700 text-white border border-indigo-500 rounded-lg hover:bg-indigo-600 transition-colors font-medium"
                >
                  <ExternalLink className="w-5 h-5" />
                  IMS Product Page
                </a>
              </div>
            </div>
            <div className="flex-1 flex justify-center">
              <div className="bg-white/10 backdrop-blur rounded-2xl p-8 border border-white/20">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/10 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-white">65%</p>
                    <p className="text-indigo-200 text-sm">Faster Approvals</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-white">$$$</p>
                    <p className="text-indigo-200 text-sm">Eliminate Overpayments</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-white">100%</p>
                    <p className="text-indigo-200 text-sm">Contract Compliance</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-white">HIPAA</p>
                    <p className="text-indigo-200 text-sm">Compliant</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        
        {/* Other Playbooks Link */}
        <div className="mb-6 flex flex-wrap gap-4 items-center justify-center">
          <p className="text-slate-600 text-sm">Other Playbooks:</p>
          <Link 
            to="/sales-playbook" 
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm font-medium"
          >
            <Target className="w-4 h-4" />
            AskIsaac Sales Playbook
          </Link>
          <Link 
            to="/onboarding-playbook" 
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition-colors text-sm font-medium"
          >
            <Users className="w-4 h-4" />
            Onboarding Playbook
          </Link>
        </div>
        
        {/* Value Proposition */}
        <div className="bg-gradient-to-r from-red-600 to-orange-500 rounded-2xl p-8 mb-8 text-white">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
            <AlertCircle className="w-7 h-7" />
            The Problem You're Solving
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white/10 backdrop-blur rounded-xl p-5">
              <h3 className="font-bold text-lg mb-2">Implant Billing Chaos</h3>
              <ul className="space-y-2 text-red-100">
                <li className="flex items-start gap-2"><AlertTriangle className="w-4 h-4 mt-1 flex-shrink-0" />Manual bill-only requests = errors & delays</li>
                <li className="flex items-start gap-2"><AlertTriangle className="w-4 h-4 mt-1 flex-shrink-0" />Vendor overpayments going undetected</li>
                <li className="flex items-start gap-2"><AlertTriangle className="w-4 h-4 mt-1 flex-shrink-0" />No visibility into implant usage & costs</li>
                <li className="flex items-start gap-2"><AlertTriangle className="w-4 h-4 mt-1 flex-shrink-0" />Contract non-compliance creates risk</li>
              </ul>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-5">
              <h3 className="font-bold text-lg mb-2">Rebate Revenue Leakage</h3>
              <ul className="space-y-2 text-red-100">
                <li className="flex items-start gap-2"><AlertTriangle className="w-4 h-4 mt-1 flex-shrink-0" />Spreadsheets can't track complex rebates</li>
                <li className="flex items-start gap-2"><AlertTriangle className="w-4 h-4 mt-1 flex-shrink-0" />Missed rebate opportunities = lost revenue</li>
                <li className="flex items-start gap-2"><AlertTriangle className="w-4 h-4 mt-1 flex-shrink-0" />No predictive analytics for optimization</li>
                <li className="flex items-start gap-2"><AlertTriangle className="w-4 h-4 mt-1 flex-shrink-0" />Manual validation errors cost thousands</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Techinquest IMS Section */}
        <Section title="💉 Techinquest IMS - Implant Management System" icon={Package} defaultOpen={true} color="indigo">
          <div className="space-y-8">
            {/* IMS HART Video Demo */}
            <div className="bg-gradient-to-r from-violet-600 to-indigo-600 rounded-2xl p-6 text-white">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <Play className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-violet-200 text-sm font-medium">FEATURED VIDEO</p>
                  <h3 className="text-xl font-bold">IMS HART Demo</h3>
                </div>
              </div>
              <div className="aspect-video rounded-xl overflow-hidden shadow-2xl">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/Ng1roAYbGJc"
                  title="IMS HART Demo Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="w-full h-full"
                />
              </div>
              <p className="text-violet-200 text-sm mt-4 text-center">
                Watch how HART transforms implant tracking, billing, and rebate management
              </p>
            </div>

            {/* Hero */}
            <div className="relative overflow-hidden rounded-2xl">
              <img 
                src="https://sterilemate.com/wp-content/uploads/2024/11/implant4.webp" 
                alt="Implant Management System" 
                className="w-full h-64 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/95 via-purple-800/80 to-transparent flex items-center">
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-white mb-2">Implant Management Software (IMS)</h3>
                  <p className="text-indigo-200 text-lg mb-4">HIPAA-Compliant Bill-Only Request Automation</p>
                  <p className="text-white/80 max-w-xl">
                    Streamlines tracking of implant usage, vendor payments, and request processing—eliminating overpayments 
                    and reducing approval delays.
                  </p>
                </div>
              </div>
            </div>

            {/* Opening Hook */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Play className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="text-lg font-bold mb-2">🎬 Opening Hook</h4>
                  <p className="text-white/90 text-lg italic">
                    "How much time does your team spend on bill-only requests every week? And how many of those have errors 
                    that delay payment or cause compliance issues? What if I told you we could cut that approval time by 65% 
                    and eliminate vendor overpayments completely?"
                  </p>
                </div>
              </div>
            </div>

            {/* Key Features */}
            <div className="space-y-4">
              <h4 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <Settings className="w-5 h-5 text-indigo-600" />
                Key Features (Demo Talking Points)
              </h4>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                <FeatureCard 
                  icon={Smartphone}
                  title="Mobile-Optimized Interface"
                  description="Manage bill-only requests anywhere, anytime. Clean, intuitive UI works on all devices."
                  color="blue"
                />
                <FeatureCard 
                  icon={Globe}
                  title="Supplier Portal"
                  description="Shifts data entry to vendors, enforcing accountability and ensuring compliance at the source."
                  color="purple"
                />
                <FeatureCard 
                  icon={Workflow}
                  title="Automated Workflow"
                  description="Reduces manual work and speeds up approvals by 65%. No more chasing down signatures."
                  color="green"
                />
                <FeatureCard 
                  icon={Shield}
                  title="Contract Compliance"
                  description="Built-in enforcement ensures every request meets hospital and vendor contract terms."
                  color="orange"
                />
                <FeatureCard 
                  icon={Link2}
                  title="System Integration"
                  description="Connects easily with EHR, ERP, and financial systems via API, HL7, and flat files."
                  color="cyan"
                />
                <FeatureCard 
                  icon={Layers}
                  title="Multi-Tier Pricing"
                  description="Control costs across multiple locations with configurable pricing levels per facility."
                  color="indigo"
                />
              </div>
            </div>

            {/* IMS Product Screenshots */}
            <div className="space-y-4">
              <h4 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-indigo-600" />
                IMS Interface Preview (Use in Demo)
              </h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="rounded-xl overflow-hidden border border-slate-200 shadow-lg bg-white">
                  <img 
                    src="https://sterilemate.com/wp-content/uploads/2024/11/implant4.webp" 
                    alt="IMS System Interface" 
                    className="w-full h-56 object-cover"
                  />
                  <div className="p-3">
                    <p className="text-sm font-medium text-slate-700">IMS Dashboard</p>
                    <p className="text-xs text-slate-500">Clean, intuitive interface for managing bill-only requests</p>
                  </div>
                </div>
                <div className="bg-indigo-50 rounded-xl p-6 border border-indigo-200 flex flex-col justify-center">
                  <h5 className="font-bold text-indigo-800 mb-3">Demo Highlights</h5>
                  <ul className="space-y-2 text-sm text-slate-700">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-indigo-600" />
                      Mobile-optimized interface
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-indigo-600" />
                      Vendor portal with accountability
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-indigo-600" />
                      Automated approval workflows
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-indigo-600" />
                      Real-time reporting dashboards
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-indigo-600" />
                      EHR/ERP integration (HL7, API)
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Coming Soon */}
            <div className="bg-gradient-to-r from-violet-100 to-purple-100 rounded-xl p-6 border border-violet-200">
              <h4 className="font-bold text-violet-800 mb-3 flex items-center gap-2">
                <Brain className="w-5 h-5" />
                Coming Soon: AI-Powered Features
              </h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-4 border border-violet-200">
                  <p className="font-semibold text-violet-700">AI Chatbot</p>
                  <p className="text-sm text-slate-600">Quick data access through natural language queries</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-violet-200">
                  <p className="font-semibold text-violet-700">Smart Reporting</p>
                  <p className="text-sm text-slate-600">AI-powered insights and anomaly detection</p>
                </div>
              </div>
            </div>

            {/* Target Buyers */}
            <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
              <h4 className="font-bold text-blue-800 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5" />
                Target Buyers & Departments
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { role: "CFO / Finance", icon: "💰" },
                  { role: "Supply Chain Director", icon: "📦" },
                  { role: "OR Manager", icon: "🏥" },
                  { role: "Materials Management", icon: "📋" },
                  { role: "Revenue Cycle", icon: "💵" },
                  { role: "Compliance Officer", icon: "✅" },
                  { role: "IT Director", icon: "💻" },
                  { role: "Surgery Center Admin", icon: "⚕️" }
                ].map((item, i) => (
                  <div key={i} className="bg-white rounded-lg p-3 text-center border border-blue-200">
                    <span className="text-2xl">{item.icon}</span>
                    <p className="text-sm font-medium text-slate-700 mt-1">{item.role}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Sales Scripts */}
            <div className="space-y-4">
              <h4 className="text-lg font-bold text-slate-800">📝 IMS Sales Scripts</h4>
              
              <ScriptCard 
                title="Discovery Question"
                script="Walk me through your current bill-only process. Who enters the data? How long does approval take? What happens when there's a discrepancy with a vendor invoice?"
                tips="Listen for pain: manual entry, delays, disputes with vendors, compliance concerns."
              />
              
              <ScriptCard 
                title="Value Pitch"
                script="Techinquest IMS automates your entire bill-only workflow. Vendors enter their own data through a secure portal—so you're not doing their work. The system validates against your contracts automatically. Approvals that used to take days now happen in hours. And every transaction is HIPAA-compliant and audit-ready."
                tips="Hit the key benefits: vendor accountability, speed, compliance, visibility."
              />

              <ScriptCard 
                title="ROI Close"
                script="Here's the math: If your team spends 20 hours a week on bill-only requests, and we cut that by 65%, that's 13 hours back. At $50/hour fully loaded, that's $33,800 a year in labor alone. Add in the overpayments you'll catch and the compliance risks you'll avoid—this system pays for itself in the first quarter."
                tips="Always quantify the ROI. Ask about their volume to personalize the calculation."
              />
            </div>

            {/* Objection Handlers */}
            <div className="space-y-3">
              <h4 className="font-semibold text-slate-800">🛡️ Objection Handlers</h4>
              <ObjectionHandler 
                objection="We already have a system"
                response="Great—what's working well, and what's frustrating? Most hospitals we work with have systems, but they're often disconnected from contracts, require manual entry, or don't give vendors accountability. IMS was built specifically for bill-only workflows. Let me show you the difference."
              />
              <ObjectionHandler 
                objection="Our vendors won't use a portal"
                response="That's what we hear initially. But here's the thing: vendors want to get paid faster. When they enter their own data correctly, invoices get approved faster. We've seen 90%+ vendor adoption within 60 days because it's in their interest. And for the holdouts, we have fallback workflows."
              />
              <ObjectionHandler 
                objection="Integration with our EHR sounds complicated"
                response="We designed IMS for healthcare from day one. We support HL7, API, and flat file integrations. We've connected to Epic, Cerner, Meditech—you name it. Our implementation team handles the heavy lifting. Most facilities are live within 8-12 weeks."
              />
            </div>
          </div>
        </Section>

        {/* HART Section */}
        <Section title="💵 H.E.A.R.T - Healthcare Earned Advanced Rebate Tracking" icon={CircleDollarSign} color="green">
          <div className="space-y-8">
            {/* Hero */}
            <div className="relative overflow-hidden rounded-2xl">
              <img 
                src="https://sterilemate.com/wp-content/uploads/2025/11/rebate-software-screenshot.webp" 
                alt="Healthcare Advanced Rebate Tracking" 
                className="w-full h-64 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/95 via-teal-800/80 to-transparent flex items-center">
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-white mb-2">Healthcare Advanced Rebate Tracking (HART)</h3>
                  <p className="text-emerald-200 text-lg mb-4">Capture Every Eligible Dollar</p>
                  <p className="text-white/80 max-w-xl">
                    Cloud-based rebate management that automates tracking, validation, and reporting—replacing manual 
                    spreadsheets with real-time accuracy and financial visibility.
                  </p>
                </div>
              </div>
            </div>

            {/* Opening Hook */}
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl p-6 text-white">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Play className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="text-lg font-bold mb-2">🎬 Opening Hook</h4>
                  <p className="text-white/90 text-lg italic">
                    "How confident are you that you're capturing every rebate dollar you're entitled to? Most hospitals 
                    we talk to are leaving 5-15% on the table because they're tracking rebates in spreadsheets. What if 
                    we could show you exactly where your money is hiding?"
                  </p>
                </div>
              </div>
            </div>

            {/* Rebate Types */}
            <div className="bg-emerald-50 rounded-xl p-6 border border-emerald-200">
              <h4 className="font-bold text-emerald-800 mb-4 flex items-center gap-2">
                <Receipt className="w-5 h-5" />
                Types of Rebates Managed (Use in Discovery)
              </h4>
              <div className="grid md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg p-4 border border-emerald-200 text-center">
                  <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <BarChart3 className="w-5 h-5 text-emerald-600" />
                  </div>
                  <p className="font-semibold text-slate-800">Volume-Based</p>
                  <p className="text-xs text-slate-500">Hit purchase thresholds</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-emerald-200 text-center">
                  <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                  </div>
                  <p className="font-semibold text-slate-800">Growth-Based</p>
                  <p className="text-xs text-slate-500">Year-over-year increases</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-emerald-200 text-center">
                  <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <PieChart className="w-5 h-5 text-emerald-600" />
                  </div>
                  <p className="font-semibold text-slate-800">Market Share</p>
                  <p className="text-xs text-slate-500">Competitive positioning</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-emerald-200 text-center">
                  <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <Layers className="w-5 h-5 text-emerald-600" />
                  </div>
                  <p className="font-semibold text-slate-800">Tiered</p>
                  <p className="text-xs text-slate-500">Multi-level thresholds</p>
                </div>
              </div>
              <p className="text-sm text-emerald-700 mt-4 italic">
                "Ask: Which of these rebate types do you currently track? How confident are you that you're hitting all your thresholds?"
              </p>
            </div>

            {/* Key Features */}
            <div className="space-y-4">
              <h4 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <Settings className="w-5 h-5 text-emerald-600" />
                Key Features (Demo Talking Points)
              </h4>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                <FeatureCard 
                  icon={Database}
                  title="Centralized Management"
                  description="Track all rebate programs across all suppliers and contracts in one place."
                  color="green"
                />
                <FeatureCard 
                  icon={Globe}
                  title="Supplier Portal"
                  description="Improves collaboration and transparency with vendors. Real-time rebate performance updates."
                  color="purple"
                />
                <FeatureCard 
                  icon={RefreshCw}
                  title="Automated Calculations"
                  description="Eliminates manual errors. System validates against contract terms automatically."
                  color="blue"
                />
                <FeatureCard 
                  icon={Brain}
                  title="Predictive Analytics"
                  description="AI identifies missed rebate opportunities and recommends optimizations."
                  color="orange"
                />
                <FeatureCard 
                  icon={LineChart}
                  title="Real-Time Reporting"
                  description="Instant visibility into spend, savings, and compliance. Custom dashboards."
                  color="cyan"
                />
                <FeatureCard 
                  icon={Shield}
                  title="Contract Enforcement"
                  description="Protects revenue by aligning every claim with negotiated terms. Audit-ready."
                  color="indigo"
                />
              </div>
            </div>

            {/* HART Product Screenshots */}
            <div className="space-y-4">
              <h4 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <LineChart className="w-5 h-5 text-emerald-600" />
                HART Interface Preview (Use in Demo)
              </h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="rounded-xl overflow-hidden border border-slate-200 shadow-lg bg-white">
                  <img 
                    src="https://sterilemate.com/wp-content/uploads/2025/11/rebate-software-screenshot.webp" 
                    alt="HART Dashboard" 
                    className="w-full h-56 object-cover object-top"
                  />
                  <div className="p-3">
                    <p className="text-sm font-medium text-slate-700">HART Dashboard</p>
                    <p className="text-xs text-slate-500">Real-time rebate tracking with visual analytics</p>
                  </div>
                </div>
                <div className="bg-emerald-50 rounded-xl p-6 border border-emerald-200 flex flex-col justify-center">
                  <h5 className="font-bold text-emerald-800 mb-3">Demo Highlights</h5>
                  <ul className="space-y-2 text-sm text-slate-700">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      Centralized rebate program tracking
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      Supplier portal for transparency
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      Automated rebate calculations
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      Predictive analytics & ML insights
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      Custom real-time reporting
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* ROI Calculator */}
            <div className="bg-gradient-to-r from-emerald-900 to-teal-800 rounded-xl p-6 text-white">
              <h4 className="text-lg font-bold text-emerald-300 mb-4 flex items-center gap-2">
                <CircleDollarSign className="w-5 h-5" />
                ROI Talking Points
              </h4>
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white/10 backdrop-blur rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-white">5-15%</p>
                  <p className="text-emerald-200 text-sm">Rebates typically missed</p>
                </div>
                <div className="bg-white/10 backdrop-blur rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-white">$$$</p>
                  <p className="text-emerald-200 text-sm">Hidden revenue recovered</p>
                </div>
                <div className="bg-white/10 backdrop-blur rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-white">90%</p>
                  <p className="text-emerald-200 text-sm">Less time on manual tracking</p>
                </div>
              </div>
              <div className="bg-white/5 rounded-lg p-4 border border-white/20">
                <p className="text-white/90 italic">
                  "If your facility spends $50 million annually with rebate-eligible suppliers, and you're missing just 5% of 
                  available rebates, that's $2.5 million sitting on the table. HART finds that money and makes sure you collect it. 
                  The system typically pays for itself within the first rebate cycle."
                </p>
              </div>
            </div>

            {/* Sales Scripts */}
            <div className="space-y-4">
              <h4 className="text-lg font-bold text-slate-800">📝 HART Sales Scripts</h4>
              
              <ScriptCard 
                title="Discovery Question"
                script="How are you currently tracking your rebate programs? Who's responsible for making sure you hit volume thresholds? When was the last time you audited whether you're collecting everything you're entitled to?"
                tips="Listen for: spreadsheets, one person who 'owns' it, lack of confidence in completeness."
              />
              
              <ScriptCard 
                title="Value Pitch"
                script="HART replaces your spreadsheets with a purpose-built rebate management platform. It automatically tracks your spend against every contract, alerts you when you're close to thresholds, and uses predictive analytics to identify rebate opportunities you're missing. Suppliers can see their performance in real-time, so there's no dispute at claim time."
                tips="Emphasize: automation, visibility, supplier accountability, predictive insights."
              />

              <ScriptCard 
                title="Partnership Close"
                script="Here's what I suggest: Let us do a rebate audit. We'll look at your top 10 supplier contracts and identify where you're leaving money on the table. No cost, no obligation. If we find significant opportunity—and we usually do—we can talk about how HART captures it going forward. When can we schedule that audit?"
                tips="Offer the free audit as a low-risk entry point. Creates urgency and demonstrates value."
              />
            </div>

            {/* Objection Handlers */}
            <div className="space-y-3">
              <h4 className="font-semibold text-slate-800">🛡️ Objection Handlers</h4>
              <ObjectionHandler 
                objection="We track rebates in Excel and it works fine"
                response="Excel works until it doesn't. What happens when the person who maintains that spreadsheet leaves? What about when a contract changes and the formulas need updating? HART gives you centralized, audit-ready tracking that doesn't depend on one person's institutional knowledge. Let's compare what you're capturing now versus what you could be."
              />
              <ObjectionHandler 
                objection="Our GPO handles our rebates"
                response="GPOs are great for negotiating contracts, but they don't track your facility-level performance in real-time. Are you confident you're hitting every tier in every contract? HART gives you visibility into your own data—so you can optimize your purchasing to maximize rebates, not just hope your GPO catches it."
              />
              <ObjectionHandler 
                objection="We don't have the bandwidth to implement new software"
                response="I hear you—everyone's stretched thin. That's exactly why HART is cloud-based with minimal IT lift. We handle the heavy lifting during implementation. Most facilities are live in 6-8 weeks. And once it's running, it saves your team hours every week on manual tracking and reconciliation."
              />
            </div>
          </div>
        </Section>

        {/* Combined Suite Section */}
        <Section title="🔗 CarelMS - The Complete Suite" icon={Layers} color="purple">
          <div className="space-y-8">
            {/* Suite Overview */}
            <div className="bg-gradient-to-r from-violet-600 to-purple-600 rounded-xl p-6 text-white">
              <h4 className="text-xl font-bold mb-4 text-center">Sterile Mate + Techinquest Suite</h4>
              <p className="text-center text-violet-200 mb-6">
                When IMS and HART work together, you get complete financial control over implants and rebates—
                eliminating leakage, accelerating cash flow, and strengthening your bottom line.
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white/10 backdrop-blur rounded-xl p-5 border border-white/20">
                  <div className="flex items-center gap-3 mb-3">
                    <Package className="w-8 h-8 text-violet-300" />
                    <h5 className="font-bold text-lg">IMS</h5>
                  </div>
                  <p className="text-violet-200">Fix implant billing at the source. Eliminate overpayments. Automate approvals.</p>
                </div>
                <div className="bg-white/10 backdrop-blur rounded-xl p-5 border border-white/20">
                  <div className="flex items-center gap-3 mb-3">
                    <CircleDollarSign className="w-8 h-8 text-emerald-300" />
                    <h5 className="font-bold text-lg">HART</h5>
                  </div>
                  <p className="text-violet-200">Capture every rebate dollar. Predictive analytics. Real-time visibility.</p>
                </div>
              </div>
            </div>

            {/* Combined Value */}
            <div className="bg-slate-900 rounded-xl p-6 text-white">
              <h4 className="text-lg font-bold text-violet-400 mb-4">Combined Value Proposition</h4>
              <div className="grid md:grid-cols-3 gap-4">
                {[
                  { icon: Shield, title: "Eliminate Financial Leakage", desc: "No more overpayments or missed rebates" },
                  { icon: Zap, title: "Accelerate Cash Flow", desc: "Faster approvals, faster rebate collection" },
                  { icon: LineChart, title: "Measurable ROI", desc: "Track savings and recovered revenue in real-time" },
                  { icon: Users, title: "Empower Leadership", desc: "Focus on patient care, not spreadsheets" },
                  { icon: Lock, title: "Ensure Compliance", desc: "Every transaction audit-ready" },
                  { icon: TrendingUp, title: "Strengthen Margins", desc: "Recover hidden revenue at scale" }
                ].map((item, i) => (
                  <div key={i} className="bg-slate-800 rounded-lg p-4 border border-slate-700">
                    <item.icon className="w-6 h-6 text-violet-400 mb-2" />
                    <p className="font-semibold text-white">{item.title}</p>
                    <p className="text-sm text-slate-400">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Suite Sales Script */}
            <ScriptCard 
              title="Suite Pitch"
              script="Most hospitals have two separate problems: implant billing chaos and rebate revenue leakage. They're usually handled by different teams with different tools. The Sterile Mate + Techinquest Suite solves both with one integrated platform. IMS stops the bleeding on implant overpayments. HART recovers the rebate dollars you're leaving on the table. Together, they give your CFO complete visibility into supply chain financials—and a measurable return on technology investment."
              tips="Use this when you've uncovered both pain points. Position the suite as the comprehensive solution."
            />
          </div>
        </Section>

        {/* Competitive Differentiation */}
        <Section title="🏆 Competitive Differentiation" icon={Target} color="orange">
          <div className="space-y-6">
            <div className="bg-amber-50 rounded-xl p-6 border border-amber-200">
              <h4 className="font-bold text-amber-800 mb-4">Why Techinquest Wins</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-4 border border-amber-200">
                  <h5 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Healthcare-Specific Design
                  </h5>
                  <p className="text-sm text-slate-600">Built for healthcare from day one—not a generic ERP bolt-on. Understands bill-only workflows, implant tracking, and healthcare rebate structures.</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-amber-200">
                  <h5 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Vendor Accountability
                  </h5>
                  <p className="text-sm text-slate-600">Supplier portal shifts data entry burden to vendors. They enter, you verify. Reduces errors and speeds payments—vendors love it.</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-amber-200">
                  <h5 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    HIPAA Compliant
                  </h5>
                  <p className="text-sm text-slate-600">Cloud-based but secure. HIPAA compliance built-in. Your data is protected.</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-amber-200">
                  <h5 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Fast Implementation
                  </h5>
                  <p className="text-sm text-slate-600">8-12 weeks typical go-live. We handle EHR/ERP integration. Minimal IT burden.</p>
                </div>
              </div>
            </div>

            <div className="bg-slate-100 rounded-xl p-6 border border-slate-200">
              <h4 className="font-bold text-slate-800 mb-4">Against Specific Competitors</h4>
              <div className="space-y-3">
                <ObjectionHandler 
                  objection="We're looking at [Generic ERP Vendor]"
                  response="ERP systems are great for general finance, but they're not built for healthcare supply chain specifics. Bill-only workflows, implant tracking, healthcare rebate structures—those require specialized solutions. That's why hospitals choose Techinquest alongside their ERP, not instead of it. We integrate seamlessly and handle what ERPs can't."
                />
                <ObjectionHandler 
                  objection="Our GPO offers rebate tracking"
                  response="GPO tools show you what's happening at the aggregate level, but you need facility-level visibility to optimize. Techinquest gives you that—plus predictive analytics to identify opportunities your GPO can't see. Think of us as the layer that maximizes what you get from your GPO contracts."
                />
              </div>
            </div>
          </div>
        </Section>

        {/* Quick Reference */}
        <div className="mt-8 bg-gradient-to-r from-indigo-900 to-purple-900 rounded-xl p-6 text-white">
          <h3 className="text-lg font-bold mb-4">Quick Reference</h3>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div>
              <h4 className="font-semibold text-indigo-300 mb-2">Key Contacts</h4>
              <p>George Broughton III</p>
              <p>Toll Free: 800.988.3892</p>
              <p>Direct: 281.456.3458</p>
            </div>
            <div>
              <h4 className="font-semibold text-indigo-300 mb-2">IMS Value Props</h4>
              <p>• 65% faster approvals</p>
              <p>• Eliminate vendor overpayments</p>
              <p>• HIPAA compliant</p>
              <p>• Vendor portal accountability</p>
            </div>
            <div>
              <h4 className="font-semibold text-indigo-300 mb-2">HART Value Props</h4>
              <p>• Capture 5-15% missed rebates</p>
              <p>• Predictive analytics</p>
              <p>• Real-time reporting</p>
              <p>• Supplier collaboration portal</p>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-indigo-700 flex items-center justify-between">
            <p className="text-indigo-300 text-sm">
              Next Step: Offer the free Rebate Audit or Bill-Only Workflow Assessment
            </p>
            <div className="flex gap-3">
              <a 
                href="https://sterilemate.com/implant-management-system/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-indigo-300 hover:text-white text-sm flex items-center gap-1"
              >
                IMS Page <ExternalLink className="w-3 h-3" />
              </a>
              <a 
                href="https://sterilemate.com/healthcare-advanced-rebate-tracking/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-indigo-300 hover:text-white text-sm flex items-center gap-1"
              >
                HART Page <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
